import React, { useRef, useState } from 'react';
import { Select, Input, InputNumber, DatePicker, Button, Tooltip, Spin, Upload, message } from 'antd';
import type { UploadFile } from 'antd';
import { CloseOutlined, PlusOutlined, InfoCircleOutlined, DeleteOutlined, PictureOutlined, FilePdfOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useMutation, useQuery } from '@tanstack/react-query';
import MapPortalService from '../../../services/MapPortal/mapportal.service';
import { useLayerProperitesContext } from '../../../context/LayerPropertiesContext';
import { seedLayerProperties } from '../LayerProperties/Symbology/symbologyConfig.utils';
import { useLegendUI } from '../../../context/MapLegendContext';
import { useCreateFeatureDraw } from '../../../hooks/useCreateFeatureDraw';
import type { DrawGeometryType, DrawnGeometry, DrawStyle } from '../../../hooks/useCreateFeatureDraw';
import { applyFeaturePickResult } from './createFeaturePick.utils';
import * as Cesium from 'cesium';
import './CreateFeatureModal.scss';
import { AreaIcon, DistanceIcon, PointIcon } from '../MapPortalMap/MeasurePanel';
import axiosInstance from '../../../interceptor/axiosInstance';
import { ApiRoutes } from '../../../routes/routeConstants/apiRoutes';
import axios from 'axios';

const { Option } = Select;

type FieldDataType = 'Integer' | 'Decimal' | 'Text' | 'Date';
type Step = 'geometry' | 'createLayer' | 'schema' | 'draw' | 'attributes' | 'saved';

interface SchemaField {
  field: string;
  label: string;
  dataType: FieldDataType;
  readOnly?: boolean;
}

interface LayerSymbology {
  fill: string;
  outline: string;
  fillOpacity: number;
  outlineOpacity: number;
  outlineWeight: number;
}

interface FeatureLayer {
  id: number;
  uniqueLayerName: string;
  layerTitle: string;
  geometryType: DrawGeometryType;
  schema: SchemaField[];
  symbology?: LayerSymbology;
}

interface ActiveLayer {
  uniqueLayerName: string;
  layerTitle: string;
  geometryType: DrawGeometryType;
  attributeFields: SchemaField[]; // editable fields only (ID excluded)
  drawStyle?: DrawStyle; // undefined → orange (new layer); set → match existing layer
}

// Draw the sketch in the target layer's symbology while appending (US: matching
// symbology). Points use the layer's FILL as the dot colour; lines/polygons use
// the outline for the stroke. A new layer uses the default orange (undefined).
const symbologyToDrawStyle = (geom: DrawGeometryType, sym?: LayerSymbology): DrawStyle | undefined => {
  if (!sym) return undefined;
  return {
    stroke: geom === 'Point' ? sym.fill : sym.outline || sym.fill,
    fill: sym.fill,
    fillAlpha: (sym.fillOpacity ?? 100) / 100,
  };
};

interface CreateFeatureModalProps {
  visible: boolean;
  setVisible: (v: boolean) => void;
  mapId?: string;
  viewerRef: any;
  setCurrentTool?: (v: string) => void;
  onLayerUpdateComplete?: () => Promise<void>;
}

const GEOMETRY_OPTIONS: { type: DrawGeometryType; label: string; icon: React.ReactNode }[] = [
  { type: 'Point', label: 'POINT', icon: <PointIcon /> },
  { type: 'Line', label: 'LINE', icon: <DistanceIcon /> },
  { type: 'Polygon', label: 'POLYGON', icon: <AreaIcon /> },
];

const NAME_PATTERN = /^[A-Za-z0-9_]+$/;

// Fly the camera to a layer's extent (so the user sees which layer they're
// editing). Bounds come from getLayerBounds, which falls back to PostGIS
// ST_Extent — empty/new layers return 0,0,0,0 and are skipped.
const flyToBounds = (viewer: Cesium.Viewer | undefined, bounds: any) => {
  if (!viewer) return;
  const minX = parseFloat(bounds?.minX ?? bounds?.minx ?? 'NaN');
  const minY = parseFloat(bounds?.minY ?? bounds?.miny ?? 'NaN');
  const maxX = parseFloat(bounds?.maxX ?? bounds?.maxx ?? 'NaN');
  const maxY = parseFloat(bounds?.maxY ?? bounds?.maxy ?? 'NaN');
  const empty = [minX, minY, maxX, maxY].some((n) => Number.isNaN(n)) || (minX === 0 && minY === 0 && maxX === 0 && maxY === 0);
  if (empty) return;
  viewer.camera.flyTo({ destination: Cesium.Rectangle.fromDegrees(minX, minY, maxX, maxY), duration: 1 });
};

// Fly the camera to a just-saved feature (US-108780 "zoom to the new feature").
const flyToGeometry = (viewer: Cesium.Viewer | undefined, geometry: DrawnGeometry) => {
  if (!viewer) return;
  const coords: number[][] = [];
  const collect = (c: any) => {
    if (typeof c[0] === 'number') coords.push(c as number[]);
    else c.forEach(collect);
  };
  collect(geometry.coordinates);
  if (!coords.length) return;
  const lons = coords.map((c) => c[0]);
  const lats = coords.map((c) => c[1]);
  const pad = 0.002;
  viewer.camera.flyTo({
    destination: Cesium.Rectangle.fromDegrees(Math.min(...lons) - pad, Math.min(...lats) - pad, Math.max(...lons) + pad, Math.max(...lats) + pad),
    duration: 1,
  });
};

/**
 * US-104110/104111/109003/109004/104113/108780 — Create Feature Tool (Phase 1:
 * select/create layer → draw → enter attributes → save). Vertex editing,
 * attachments, and unsaved-change confirmations are handled in a later phase.
 */
const CreateFeatureModal: React.FC<CreateFeatureModalProps> = ({ visible, setVisible, mapId, viewerRef, setCurrentTool, onLayerUpdateComplete }) => {
  const {
    getFeatureLayers,
    createFeatureLayer,
    createFeature,
    updateFeature,
    deleteFeature,
    getFeatureAt,
    uploadFeatureAttachments,
    getLayerBounds,
  } = MapPortalService();
  const layerProps = useLayerProperitesContext();
  const { setModalVisible } = useLegendUI();

  const [step, setStep] = useState<Step>('geometry');
  const [geometryType, setGeometryType] = useState<DrawGeometryType | null>(null);
  const [layerChoice, setLayerChoice] = useState<string | null>(null); // uniqueLayerName | "new"
  const [newLayerName, setNewLayerName] = useState('');
  const [fields, setFields] = useState<SchemaField[]>([{ field: 'ID', label: 'ID', dataType: 'Integer', readOnly: true }]);
  const [activeLayer, setActiveLayer] = useState<ActiveLayer | null>(null);
  const [drawnGeometry, setDrawnGeometry] = useState<DrawnGeometry | null>(null);
  const [attributeValues, setAttributeValues] = useState<Record<string, unknown>>({});
  const [attachments, setAttachments] = useState<UploadFile[]>([]); // US-109002
  // US-108778: gid + loaded geometry when editing an EXISTING saved feature.
  const [editingGid, setEditingGid] = useState<number | null>(null);
  const [editGeometry, setEditGeometry] = useState<DrawnGeometry | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  // US-109004: a layer created during this session — its Layer Properties open
  // on exit so the user can set symbology right away.
  const [createdLayer, setCreatedLayer] = useState<Record<string, unknown> | null>(null);
  const [error, setError] = useState<string>('');
  // Bumps on each pick request so a slow response can't override a newer click
  // or a draw the user already finished (non-blocking onRequestEditAt).
  const pickGenRef = useRef(0);

  // Existing compatible layers (US-104111) — fetched once a geometry type is chosen.
  const { data: existingLayers = [], isFetching: layersLoading } = useQuery({
    ...getFeatureLayers(mapId, geometryType ?? undefined),
    enabled: visible && step === 'geometry' && !!mapId,
  });

  const createLayerMut = useMutation(createFeatureLayer());
  const createFeatureMut = useMutation(createFeature());
  const updateFeatureMut = useMutation(updateFeature());
  const deleteFeatureMut = useMutation(deleteFeature());
  const uploadAttachmentsMut = useMutation(uploadFeatureAttachments());
  const featureAtMut = useMutation(getFeatureAt());
  const layerBoundsMut = useMutation(getLayerBounds());

  // Zoom to a selected existing layer so the user sees what they're editing.
  const zoomToLayer = async (layerName: string) => {
    try {
      const bounds = await layerBoundsMut.mutateAsync({ layerName });
      flyToBounds(viewerRef?.current, bounds);
    } catch {
      // bounds fetch failed — leave the camera as-is
    }
  };

  // Drawing is active on the draw step, and on the attributes step while EDITING
  // a saved feature (so its nodes stay draggable). Geometry type comes from the layer.
  const drawType = activeLayer?.geometryType ?? geometryType;
  const isEditingExisting = editingGid != null;
  const { clearDrawn, getGeometry } = useCreateFeatureDraw({
    viewerRef,
    active: visible && !!drawType && (step === 'draw' || (step === 'attributes' && isEditingExisting)),
    geometryType: drawType,
    style: activeLayer?.drawStyle, // orange for new layers, target symbology when appending
    editGeometry: isEditingExisting ? editGeometry : null,
    onComplete: (geom) => {
      setDrawnGeometry(geom);
      setAttributeValues({});
      setAttachments([]);
      setStep('attributes');
    },
    // US-108778: click near an existing feature → open for editing. Fired in the
    // background by the draw hook so vertex placement is never network-bound.
    onRequestEditAt: async (lng, lat, tolMeters) => {
      if (!activeLayer) return false;
      const gen = ++pickGenRef.current;
      try {
        const feature = await featureAtMut.mutateAsync({
          layerName: activeLayer.uniqueLayerName,
          lng,
          lat,
          tol: tolMeters,
        });
        const result = applyFeaturePickResult({
          requestGen: gen,
          currentGen: pickGenRef.current,
          feature,
          geometryType: activeLayer.geometryType,
          schemaFields: activeLayer.attributeFields.map((f) => f.field),
        });
        if (!result.applied) return false;
        setEditingGid(result.gid);
        setEditGeometry(result.geometry);
        setDrawnGeometry(result.geometry);
        setAttributeValues(result.attributeValues);
        setAttachments([]);
        setStep('attributes');
        return true;
      } catch {
        return false;
      }
    },
  });

  const resetAll = () => {
    setStep('geometry');
    setGeometryType(null);
    setLayerChoice(null);
    setNewLayerName('');
    setFields([{ field: 'ID', label: 'ID', dataType: 'Integer', readOnly: true }]);
    setActiveLayer(null);
    setDrawnGeometry(null);
    setAttributeValues({});
    setAttachments([]);
    setEditingGid(null);
    setEditGeometry(null);
    setConfirmDelete(false);
    setCreatedLayer(null);
    setError('');
    clearDrawn();
  };

  // US-109004: open the new layer's Layer Properties so the user can set its
  // symbology right away (Figma: quitting the tool prompts Layer Properties).
  const openLayerProperties = (layer: Record<string, unknown>) => {
    layerProps.setState(() => seedLayerProperties(layer));
    setModalVisible(true);
  };

  const close = () => {
    const newLayer = createdLayer; // captured before reset clears it
    resetAll();
    setVisible(false);
    setCurrentTool?.('');
    if (newLayer) openLayerProperties(newLayer);
  };

  // US-109005 / US-108781: unsaved work = a drawn (unsaved) feature, entered
  // attributes, or an in-progress new-layer config (name / custom fields).
  const isLayerConfigStep = step === 'createLayer' || step === 'schema';
  const hasUnsavedChanges =
    !!drawnGeometry ||
    Object.keys(attributeValues).length > 0 ||
    attachments.length > 0 ||
    (isLayerConfigStep && (!!newLayerName.trim() || fields.some((f) => !f.readOnly)));

  // Exit confirmation (Continue Editing / Discard Changes). Layer-creation steps
  // get the "Create New Feature Tool" wording (US-109005); editing steps get the
  // generic unsaved-changes wording (US-108781).
  const [confirmExit, setConfirmExit] = useState(false);
  const requestClose = () => {
    if (hasUnsavedChanges) setConfirmExit(true);
    else close();
  };
  const exitMessage = isEditingExisting
    ? 'Discard all unsaved geometry changes?'
    : isLayerConfigStep
      ? 'Exit the Create New Feature Tool? Any unfinished features will not be saved!'
      : 'You have unsaved changes. Do you want to discard them?';

  // Back out of editing a saved feature → return to drawing without changes.
  const exitEdit = () => {
    setEditingGid(null);
    setEditGeometry(null);
    setDrawnGeometry(null);
    setAttributeValues({});
    setAttachments([]);
    clearDrawn();
    setStep('draw');
  };

  if (!visible) return null;

  // ---- step transitions ----------------------------------------------------

  const handleGeometryNext = () => {
    if (!geometryType) {
      setError('Please select a geometry type!');
      return;
    }
    if (!layerChoice) {
      setError('Please select a layer.');
      return;
    }
    setError('');
    if (layerChoice === 'new') {
      setStep('createLayer');
      return;
    }
    const layer = (existingLayers as FeatureLayer[]).find((l) => l.uniqueLayerName === layerChoice);
    if (!layer) {
      setError('Selected layer is no longer available.');
      return;
    }
    setActiveLayer({
      uniqueLayerName: layer.uniqueLayerName,
      layerTitle: layer.layerTitle,
      geometryType: layer.geometryType,
      attributeFields: (layer.schema ?? []).filter((f) => !f.readOnly),
      drawStyle: symbologyToDrawStyle(layer.geometryType, layer.symbology),
    });
    setStep('draw');
  };

  const handleCreateLayerNext = () => {
    const name = newLayerName.trim();
    if (!name) return setError('Layer name is required');
    if (name.length > 50) return setError('Layer name cannot exceed 50 characters');
    if (!NAME_PATTERN.test(name)) return setError('Use only letters, numbers, and underscores.');
    setError('');
    setStep('schema');
  };

  const handleSchemaNext = async () => {
    // US-109003 validation: each custom field needs a unique, non-empty name.
    const seen = new Set<string>(['id']);
    for (const f of fields.filter((x) => !x.readOnly)) {
      const key = f.label.trim().toLowerCase();
      if (!key) return setError('Field name is required');
      if (key.length > 10) return setError('Field name cannot exceed 10 characters');
      if (seen.has(key)) return setError('Field name already exists.');
      seen.add(key);
    }
    setError('');

    try {
      const customFields = fields.filter((f) => !f.readOnly).map((f) => ({ name: f.label.trim(), dataType: f.dataType }));
      const result = await createLayerMut.mutateAsync({
        mapId: String(mapId),
        layerName: newLayerName.trim(),
        geometryType: geometryType as DrawGeometryType,
        fields: customFields,
      });
      const created = result?.layer;
      setCreatedLayer(created ?? null); // its Layer Properties open on exit (US-109004)
      const fp = (created?.fieldProperties ?? []) as { field: string; alias: string; dataType: FieldDataType }[];
      setActiveLayer({
        uniqueLayerName: created.uniqueLayerName,
        layerTitle: created.layerTitle ?? newLayerName.trim(),
        geometryType: geometryType as DrawGeometryType,
        attributeFields: fp.map((p) => ({ field: p.field, label: p.alias, dataType: p.dataType })),
      });
      await onLayerUpdateComplete?.(); // new layer → map + legend
      setStep('draw');
    } catch {
      // notification handled by the service; stay on the schema step for retry
    }
  };

  const minVertexError = (g: DrawGeometryType): string =>
    g === 'Line' ? 'Line must contain at least 2 vertices.' : 'Polygon must contain at least 3 vertices.';

  const getFileNameFromUrl = (url: any) => {
    if (!url || typeof url !== 'string') return '';
    const parts = url.split('/');
    return decodeURIComponent(parts[parts.length - 1]);
  };

  const handleSaveFeature = async () => {
    if (!activeLayer) return;

    setError('');
    try {
      let gid: number | null = editingGid;
      const currentGeometry = getGeometry() || editGeometry || drawnGeometry;

      if (!currentGeometry) {
        setError(minVertexError(activeLayer.geometryType));
        return;
      }

      // 1. INITIAL SAVE: Create or Update to ensure we have a GID
      if (isEditingExisting) {
        await updateFeatureMut.mutateAsync({
          layerName: activeLayer.uniqueLayerName,
          gid: editingGid as number,
          geometry: currentGeometry,
          properties: attributeValues,
        });
      } else {
        const createResult = await createFeatureMut.mutateAsync({
          layerName: activeLayer.uniqueLayerName,
          geometry: currentGeometry,
          properties: attributeValues,
        });
        gid = createResult.gid;
      }

      // 2. ATTACHMENT UPLOAD
      if (attachments.length > 0 && gid) {
        const file = attachments[0].originFileObj as File;
        const fileExtension = file.name.split('.').pop();
        const s3Key = `attachments/${activeLayer.uniqueLayerName}/${gid}/${file.name}`;

        // A. Get BOTH URLs from Backend
        const { data } = await axiosInstance.post(ApiRoutes.PRESIGNED_URL, {
          fileName: s3Key,
          fileType: fileExtension,
        });

        // B. Upload using 'presignedUrl'
        await axios.put(data.presignedUrl.presignedUrl, file, {
          headers: { 'Content-Type': file.type },
        });

        // C. Save the 'publicUrl' returned by the backend
        // No more construction logic or bucket names in frontend!
        await updateFeatureMut.mutateAsync({
          layerName: activeLayer.uniqueLayerName,
          gid: gid,
          properties: {
            ...attributeValues,
            attachment: data.presignedUrl.publicUrl, // Just pass the value directly from the response
          },
        });
      }

      // 3. FINALIZE
      await onLayerUpdateComplete?.();
      flyToGeometry(viewerRef?.current, currentGeometry as DrawnGeometry);
      clearDrawn();
      setStep('saved');
    } catch (err: any) {
      console.error('Save Feature failed:', err);
      setError(err?.response?.data?.message || 'Unable to save feature.');
    }
  };

  // US-108778 delete control — permanently remove the feature being edited.
  const handleDeleteFeature = async () => {
    if (!activeLayer || editingGid == null) return;
    try {
      await deleteFeatureMut.mutateAsync({ layerName: activeLayer.uniqueLayerName, gid: editingGid });
      await onLayerUpdateComplete?.();
      setConfirmDelete(false);
      exitEdit();
    } catch {
      setConfirmDelete(false);
    }
  };

  const addAnotherFeature = () => {
    setDrawnGeometry(null);
    setAttributeValues({});
    setAttachments([]);
    setEditingGid(null);
    setEditGeometry(null);
    clearDrawn();
    setStep('draw');
  };

  // US-109002: only JPG/PNG, ≤ 10 MB. Reject invalid files with the exact AC
  // message and keep them out of the list (validation also enforced server-side).
  const beforeAttachmentUpload = (file: File): boolean | typeof Upload.LIST_IGNORE => {
    const okType = file.type === 'image/jpeg' || file.type === 'image/png';
    if (!okType) {
      message.error('Unsupported file type. Only JPG and PNG files are allowed.');
      return Upload.LIST_IGNORE;
    }
    if (file.size > 10 * 1024 * 1024) {
      message.error('File size exceeds the maximum allowed limit of 10 MB.');
      return Upload.LIST_IGNORE;
    }
    return false; // keep in the list but don't auto-upload; we send on save
  };

  // ---- schema builder helpers ----------------------------------------------
  const addField = () => setFields((p) => [...p, { field: '', label: '', dataType: 'Text' }]);
  const removeField = (i: number) => setFields((p) => p.filter((_, idx) => idx !== i));
  const updateFieldName = (i: number, v: string) => setFields((p) => p.map((f, idx) => (idx === i ? { ...f, label: v, field: v } : f)));
  const updateFieldType = (i: number, v: FieldDataType) => setFields((p) => p.map((f, idx) => (idx === i ? { ...f, dataType: v } : f)));

  const busy =
    createLayerMut.isPending ||
    createFeatureMut.isPending ||
    updateFeatureMut.isPending ||
    deleteFeatureMut.isPending ||
    uploadAttachmentsMut.isPending;

  const layerLabel = activeLayer?.layerTitle ?? newLayerName;

  // ---- render ---------------------------------------------------------------
  const renderAttributeInput = (f: SchemaField) => {
    const val = attributeValues[f.field];
    const set = (v: unknown) => setAttributeValues((p) => ({ ...p, [f.field]: v }));
    if (f.dataType === 'Integer') return <InputNumber precision={0} value={val as number} onChange={(v) => set(v)} className="cf-panel__input" />;
    if (f.dataType === 'Decimal') return <InputNumber value={val as number} onChange={(v) => set(v)} className="cf-panel__input" />;
    if (f.dataType === 'Date')
      return (
        <DatePicker value={val ? dayjs(val as string) : null} onChange={(d) => set(d ? d.format('YYYY-MM-DD') : null)} className="cf-panel__input" />
      );
    return <Input value={val as string} onChange={(e) => set(e.target.value)} className="cf-panel__input" />;
  };

  return (
    <div className="cf-panel">
      <button type="button" className="cf-panel__close" onClick={requestClose} aria-label="Close">
        <CloseOutlined />
      </button>

      {confirmExit && (
        <div className="cf-panel__confirm">
          <div className="cf-panel__confirm-box">
            <p className="cf-panel__confirm-msg">{exitMessage}</p>
            <div className="cf-panel__confirm-actions">
              <Button className="cf-panel__btn" onClick={() => setConfirmExit(false)}>
                Continue Editing
              </Button>
              <Button
                className="cf-panel__btn cf-panel__btn--primary"
                onClick={() => {
                  setConfirmExit(false);
                  close();
                }}
              >
                Discard Changes
              </Button>
            </div>
          </div>
        </div>
      )}

      {confirmDelete && (
        <div className="cf-panel__confirm">
          <div className="cf-panel__confirm-box">
            <p className="cf-panel__confirm-msg">PERMANENTLY DELETE THIS FEATURE? THIS CANNOT BE UNDONE.</p>
            <div className="cf-panel__confirm-actions">
              <Button className="cf-panel__btn" disabled={busy} onClick={() => setConfirmDelete(false)}>
                Back
              </Button>
              <Button className="cf-panel__btn cf-panel__btn--primary" loading={busy} onClick={handleDeleteFeature}>
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* STEP 1 — geometry + layer */}
      {step === 'geometry' && (
        <>
          <div className="cf-panel__title">CREATE FEATURE</div>
          <div className="cf-panel__geom-row">
            {GEOMETRY_OPTIONS.map((g) => (
              <button
                key={g.type}
                type="button"
                className={`cf-panel__geom ${geometryType === g.type ? 'is-active' : ''}`}
                onClick={() => {
                  if (geometryType === g.type) {
                    // Deselect if already active
                    setGeometryType(null);
                    setLayerChoice(null);
                  } else {
                    // Select and default to "Create New"
                    setGeometryType(g.type);
                    setLayerChoice('new');
                  }
                  setError('');
                }}
              >
                <span className="cf-panel__geom-icon">{g.icon}</span>
                {g.label}
              </button>
            ))}
          </div>
          <Select
            placeholder="SELECT LAYER"
            className="cf-panel__select"
            value={layerChoice}
            // disabled={!geometryType}
            loading={layersLoading}
            onChange={(v) => {
              setLayerChoice(v);
              setError('');
              if (v && v !== 'new') {
                const selected = (existingLayers as FeatureLayer[]).find((l) => l.uniqueLayerName === v);
                if (selected) {
                  setGeometryType(selected.geometryType);
                }
                zoomToLayer(v);
              } // US: zoom so the user sees the layer
            }}
            notFoundContent={layersLoading ? <Spin size="small" /> : 'No matching layers available.'}
            showSearch
            optionFilterProp="children"
          >
            {(existingLayers as FeatureLayer[]).map((l) => (
              <Option key={l.uniqueLayerName} value={l.uniqueLayerName}>
                {l.layerTitle}
              </Option>
            ))}
            <Option value="new">CREATE NEW…</Option>
          </Select>
          {error && <div className="cf-panel__error">{error}</div>}
          <div className="cf-panel__footer">
            <Button className="cf-panel__btn cf-panel__btn--primary" onClick={handleGeometryNext}>
              Next
            </Button>
          </div>
        </>
      )}

      {/* STEP 2 — create layer (name + read-only type) */}
      {step === 'createLayer' && (
        <>
          <div className="cf-panel__title">CREATE LAYER</div>
          <label className="cf-panel__label">LAYER TYPE</label>
          <Select className="cf-panel__select" value={geometryType ?? undefined} disabled>
            {GEOMETRY_OPTIONS.map((g) => (
              <Option key={g.type} value={g.type}>
                {g.label}
              </Option>
            ))}
          </Select>
          <label className="cf-panel__label">
            LAYER NAME{' '}
            <Tooltip title="Use only letters, numbers, and underscores (_). Layer names cannot contain spaces or special characters.">
              <InfoCircleOutlined />
            </Tooltip>
          </label>
          <Input
            className="cf-panel__input"
            value={newLayerName}
            maxLength={50}
            showCount
            onChange={(e) => {
              setNewLayerName(e.target.value);
              setError('');
            }}
          />
          {error && <div className="cf-panel__error">{error}</div>}
          <div className="cf-panel__footer cf-panel__footer--split">
            <Button className="cf-panel__btn" onClick={() => setStep('geometry')}>
              Back
            </Button>
            <Button className="cf-panel__btn cf-panel__btn--primary" onClick={handleCreateLayerNext}>
              Next
            </Button>
          </div>
        </>
      )}

      {/* STEP 3 — schema */}
      {step === 'schema' && (
        <>
          <div className="cf-panel__title">CREATE LAYER</div>
          <div className="cf-panel__schema-head">
            <span>FIELD</span>
            <span>DATA TYPE</span>
          </div>
          {fields.map((f, i) => (
            <div key={i} className="cf-panel__schema-row">
              <Input
                className={`cf-panel__input ${f.readOnly ? 'is-readonly' : ''}`}
                value={f.label}
                disabled={f.readOnly}
                maxLength={10}
                onChange={(e) => updateFieldName(i, e.target.value)}
              />
              <Select className="cf-panel__type" value={f.dataType} disabled={f.readOnly} onChange={(v) => updateFieldType(i, v)}>
                <Option value="Integer">INTEGER</Option>
                <Option value="Decimal">DECIMAL</Option>
                <Option value="Text">TEXT</Option>
                <Option value="Date">DATE</Option>
              </Select>
              {!f.readOnly && (
                <button type="button" className="cf-panel__remove" onClick={() => removeField(i)} aria-label="Remove field">
                  <CloseOutlined />
                </button>
              )}
            </div>
          ))}
          <button type="button" className="cf-panel__add-field" onClick={addField}>
            <PlusOutlined /> ADD FIELD
          </button>
          {error && <div className="cf-panel__error">{error}</div>}
          <div className="cf-panel__footer cf-panel__footer--split">
            <Button className="cf-panel__btn" onClick={() => setStep('createLayer')}>
              Back
            </Button>
            <Button className="cf-panel__btn cf-panel__btn--primary" loading={busy} onClick={handleSchemaNext}>
              Next
            </Button>
          </div>
        </>
      )}

      {/* STEP 4 — drawing instructions */}
      {step === 'draw' && (
        <>
          <div className="cf-panel__title">CREATE FEATURE</div>
          <div className="cf-panel__layer-name">LAYER: {layerLabel}</div>
          <ul className="cf-panel__instructions">
            <li>Use the cursor to create a feature.</li>
            {drawType === 'Point' ? (
              <>
                <li>Click on the map to place the point, click again to finish drawing.</li>
              </>
            ) : (
              <>
                <li>Click to add points, then double-click to finish drawing.</li>
              </>
            )}
            <li>Click and drag a node to move it.</li>
            <li>Right-click a node to remove it.</li>
            <li>Click an existing feature to edit it.</li>
          </ul>
          <div className="cf-panel__footer cf-panel__footer--split">
            <Button
              className="cf-panel__btn"
              onClick={() => {
                clearDrawn();
                setStep(activeLayer && layerChoice !== 'new' ? 'geometry' : 'schema');
              }}
            >
              Back
            </Button>
            <Button className="cf-panel__btn" onClick={requestClose}>
              Done
            </Button>
          </div>
        </>
      )}

      {/* STEP 5 — attributes */}
      {step === 'attributes' && activeLayer && (
        <>
          <div className="cf-panel__title">{isEditingExisting ? 'EDIT FEATURE' : 'CREATE FEATURE'}</div>
          <div className="cf-panel__layer-name">
            LAYER: {activeLayer.layerTitle.toUpperCase()}
            {isEditingExisting && (
              <button
                type="button"
                className="cf-panel__trash"
                onClick={() => setConfirmDelete(true)}
                aria-label="Delete feature"
                title="Delete feature"
              >
                <DeleteOutlined />
              </button>
            )}
          </div>
          {isEditingExisting && <div className="cf-panel__hint">Drag a node to move it, right-click a node to remove it.</div>}
          {activeLayer.attributeFields.length === 0 && <div className="cf-panel__hint">This layer has no attribute fields.</div>}
          {activeLayer.attributeFields
            .filter((f) => f.field !== 'attachment')
            .map((f) => (
              <div key={f.field} className="cf-panel__attr-row">
                <div className="cf-panel__label-row">
                  <label className="cf-panel__attr-label">{f.label.toUpperCase()}</label>
                  <span className="cf-panel__attr-hint">
                    {f.field}; {f.dataType.toLowerCase()}
                  </span>
                </div>
                {renderAttributeInput(f)}
              </div>
            ))}
          {/* Attachment Section */}
          <div className="cf-panel__attr-row">
            <div className="cf-panel__label-row">
              <label className="cf-panel__attr-label">ATTACH A FILE</label>

              {/* Show upload trigger ONLY if nothing is in session AND nothing is in DB state */}
              {!attachments.length && !attributeValues.attachment && (
                <Upload
                  accept="image/jpeg,image/png,application/pdf"
                  showUploadList={false}
                  beforeUpload={beforeAttachmentUpload}
                  onChange={({ fileList }) => setAttachments(fileList)}
                >
                  <div className="cf-panel__upload-icons">
                    <PictureOutlined />
                  </div>
                </Upload>
              )}
            </div>

            {attachments.length > 0 && (
              <div className="cf-panel__file-preview">
                <div className="cf-panel__file-info">
                  <PictureOutlined className="cf-panel__file-icon" />
                  <span className="cf-panel__file-name">{attachments[0].name}</span>
                </div>
                <CloseOutlined className="cf-panel__file-remove" onClick={() => setAttachments([])} />
              </div>
            )}

            {!attachments.length && (attributeValues?.attachment as any) && (
              <div className="cf-panel__file-preview">
                <div
                  className="cf-panel__file-info"
                  style={{ cursor: 'pointer' }}
                  title="Click to view attachment"
                  onClick={() => window.open(attributeValues.attachment as string, '_blank')}
                >
                  {/* Logic to show correct icon based on extension */}
                  {String(attributeValues.attachment).match(/\.(pdf)$/i) ? (
                    <FilePdfOutlined className="cf-panel__file-icon" />
                  ) : (
                    <PictureOutlined className="cf-panel__file-icon" />
                  )}
                  <span className="cf-panel__file-name">{getFileNameFromUrl(attributeValues.attachment)}</span>
                </div>
                <CloseOutlined className="cf-panel__file-remove" onClick={() => setAttributeValues((prev) => ({ ...prev, attachment: '' }))} />
              </div>
            )}
          </div>
          {error && <div className="cf-panel__error">{error}</div>}
          <div className="cf-panel__footer cf-panel__footer--split">
            <Button className="cf-panel__btn" disabled={busy} onClick={isEditingExisting ? exitEdit : addAnotherFeature}>
              Back
            </Button>
            <Button className="cf-panel__btn cf-panel__btn--primary" loading={busy} onClick={handleSaveFeature}>
              {isEditingExisting ? 'Save' : 'Finish'}
            </Button>
          </div>
        </>
      )}

      {/* saved — add another / finish */}
      {step === 'saved' && (
        <>
          <div className="cf-panel__title">FEATURE SAVED</div>
          <div className="cf-panel__layer-name">LAYER: {activeLayer?.layerTitle}</div>
          <div className="cf-panel__footer cf-panel__footer--split">
            <Button className="cf-panel__btn cf-panel__btn--primary" onClick={addAnotherFeature}>
              Add Another Feature
            </Button>
            <Button className="cf-panel__btn" onClick={close}>
              Finish Editing Layer
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default CreateFeatureModal;
